"use client";

import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { CUSTOMER_TYPE_IDS } from "@/lib/constants/customer-types";
import { copyReviewQuestionsFromShow } from "@/lib/reviews/admin-actions";

/**
 * "Copy review questions from another show" picker — mirrors
 * ImportTemplatesDialog (email templates).
 *
 * Copies the selected show's questions into this show, replacing whatever this
 * show currently has (per-show storage via `review_questions.show_id`).
 */

interface ImportReviewQuestionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  venueId: number;
  showId: number;
  onImported: () => void;
}

interface ShowOption {
  id: number;
  name: string;
}

const getCustomerTypeLabel = (customerTypeId?: number): string | null => {
  if (customerTypeId === CUSTOMER_TYPE_IDS.PRIVATE) return "Private";
  if (customerTypeId === CUSTOMER_TYPE_IDS.CORPORATE) return "Corporate";
  return null;
};

const getShowName = (show: any): string => {
  const infos = show?.show_infos || [];
  const preferred =
    infos.find((i: any) => i.lang === "en")?.name || infos[0]?.name;
  const name = preferred || `Show #${show?.id}`;
  const typeLabel = getCustomerTypeLabel(show?.customer_type_id);
  return typeLabel ? `${name} - ${typeLabel}` : name;
};

export function ImportReviewQuestionsDialog({
  open,
  onOpenChange,
  venueId,
  showId,
  onImported,
}: ImportReviewQuestionsDialogProps) {
  const [shows, setShows] = useState<ShowOption[]>([]);
  const [isLoadingShows, setIsLoadingShows] = useState(false);
  const [selectedShowId, setSelectedShowId] = useState<string>("");
  const [isImporting, setIsImporting] = useState(false);

  // Load the venue's other shows when the dialog opens.
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const fetchShows = async () => {
      setIsLoadingShows(true);
      try {
        const response = await fetch(`/api/venues/${venueId}/shows`);
        if (!response.ok) throw new Error("Failed to fetch shows");
        const { shows: fetchedShows } = await response.json();

        const options: ShowOption[] = (fetchedShows || [])
          .filter((show: any) => show.id !== showId)
          .map((show: any) => ({ id: show.id, name: getShowName(show) }))
          .sort((a: ShowOption, b: ShowOption) => a.name.localeCompare(b.name));

        if (!cancelled) setShows(options);
      } catch (error) {
        console.error("Error fetching shows for import:", error);
        if (!cancelled) toast.error("Failed to load shows");
      } finally {
        if (!cancelled) setIsLoadingShows(false);
      }
    };

    setSelectedShowId("");
    fetchShows();

    return () => {
      cancelled = true;
    };
  }, [open, venueId, showId]);

  const handleImport = async () => {
    if (!selectedShowId) {
      toast.error("Please select a show to copy from");
      return;
    }

    setIsImporting(true);
    try {
      const result = await copyReviewQuestionsFromShow(
        venueId,
        Number(selectedShowId),
        showId,
      );
      if (result.success) {
        toast.success("Review questions copied");
        onOpenChange(false);
        onImported();
      } else {
        toast.error(result.error ?? "Could not copy questions");
      }
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Copy review questions</DialogTitle>
          <DialogDescription>
            Copy the review/feedback questions from another show in this venue.
            This replaces the current show&apos;s questions with copies of the
            selected show&apos;s set.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Copy from
          </label>
          <Select
            value={selectedShowId}
            onValueChange={setSelectedShowId}
            disabled={isLoadingShows || isImporting}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  isLoadingShows ? "Loading shows..." : "Select a show"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {shows.map((show) => (
                <SelectItem key={show.id} value={show.id.toString()}>
                  {show.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {!isLoadingShows && shows.length === 0 && (
            <p className="text-sm text-gray-500 mt-2">
              No other shows are available in this venue.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isImporting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={!selectedShowId || isImporting}
          >
            {isImporting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Copy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
